import { apiClient } from './apiClient';
import { API_ENDPOINTS } from '../constants';
import type { ApiResponse } from '@/types';
import type { WorkflowDefinitionV1 } from '../types/workflow-spec';
import { buildListQuery, type ListParams, type Paginated } from './agentService';

export interface WorkflowSummary {
  id: string;
  name: string;
  description?: string;
  status: string;
  nodeCount: number;
  agentNames: string[];
  createdAt: string;
  updatedAt: string;
}

export type WorkflowDefinition = WorkflowDefinitionV1;

export interface WorkflowAgentInstance {
  id: string;
  name: string;
  code: string;
  description?: string | null;
  entryType?: string;
  agentRole: 'entry' | 'functional';
  version: string;
  isActive: boolean;
  agentType?: { code: string; name: string };
}

export interface WorkflowRunSummary {
  id: string;
  workflowId: string;
  tenantId: string;
  workflowVersion: number;
  channel: string;
  entryType?: string;
  externalSessionId?: string;
  conversationId?: string;
  status: string;
  currentNodeId?: string;
  resolvedIntent?: string;
  resolvedCapabilityCode?: string;
  startedAt: string;
  completedAt?: string;
  stepCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowRunEvent {
  id: string;
  type: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface WorkflowRunStep {
  id: string;
  nodeId: string;
  nodeKind: string;
  status: string;
  input: Record<string, unknown>;
  output: Record<string, unknown>;
  startedAt: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowRunDetail extends WorkflowRunSummary {
  metadata: Record<string, unknown>;
  steps: WorkflowRunStep[];
  events: WorkflowRunEvent[];
}

export interface CreateWorkflowRunInput {
  message: string;
  conversationId?: string;
  clientId?: string;
  additionalInputParams?: Record<string, unknown>;
  userTenantProfileId?: string;
}

export const workflowService = {
  async list(params?: ListParams & { status?: string }): Promise<Paginated<WorkflowSummary>> {
    const query = buildListQuery(params);
    const url = query ? `${API_ENDPOINTS.WORKFLOWS.LIST}?${query}` : API_ENDPOINTS.WORKFLOWS.LIST;
    const result = await apiClient.get<ApiResponse<Paginated<WorkflowSummary>>>(url, { authenticate: true });
    return (result as any).data ?? result;
  },

  async getById(id: string): Promise<WorkflowDefinition> {
    const result = await apiClient.get<ApiResponse<WorkflowDefinition>>(
      API_ENDPOINTS.WORKFLOWS.GET_BY_ID(encodeURIComponent(id)),
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async create(payload: Omit<WorkflowDefinition, 'id' | 'createdAt' | 'updatedAt'>): Promise<WorkflowDefinition> {
    const result = await apiClient.post<ApiResponse<WorkflowDefinition>>(
      API_ENDPOINTS.WORKFLOWS.CREATE,
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async update(id: string, payload: Partial<WorkflowDefinition>): Promise<WorkflowDefinition> {
    const result = await apiClient.put<ApiResponse<WorkflowDefinition>>(
      API_ENDPOINTS.WORKFLOWS.UPDATE(encodeURIComponent(id)),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async delete(id: string): Promise<{ success: boolean }> {
    const result = await apiClient.delete<ApiResponse<{ success: boolean }>>(
      API_ENDPOINTS.WORKFLOWS.DELETE(encodeURIComponent(id)),
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  // `workflowId` scopes the list to the workflow being edited: a workflow created
  // from an AI Pod subscription only offers that pod's agents.
  async listEntryAgents(
    triggerType?: 'manual' | 'webhook' | 'inbound-call' | 'outbound-call' | 'whatsapp',
    workflowId?: string
  ): Promise<WorkflowAgentInstance[]> {
    const params = new URLSearchParams();
    if (triggerType) params.set('triggerType', triggerType);
    if (workflowId) params.set('workflowId', workflowId);
    const query = params.toString();
    const url = query
      ? `${API_ENDPOINTS.WORKFLOWS.AGENTS.ENTRY}?${query}`
      : API_ENDPOINTS.WORKFLOWS.AGENTS.ENTRY;
    const result = await apiClient.get<ApiResponse<{ items: WorkflowAgentInstance[] }>>(url, { authenticate: true });
    const payload = (result as any).data ?? result;
    return payload.items ?? [];
  },

  async listFunctionalAgents(workflowId?: string): Promise<WorkflowAgentInstance[]> {
    const url = workflowId
      ? `${API_ENDPOINTS.WORKFLOWS.AGENTS.FUNCTIONAL}?workflowId=${encodeURIComponent(workflowId)}`
      : API_ENDPOINTS.WORKFLOWS.AGENTS.FUNCTIONAL;
    const result = await apiClient.get<ApiResponse<{ items: WorkflowAgentInstance[] }>>(
      url,
      { authenticate: true }
    );
    const payload = (result as any).data ?? result;
    return payload.items ?? [];
  },

  async listRuns(workflowId: string, params?: ListParams & { status?: string; channel?: string }): Promise<Paginated<WorkflowRunSummary>> {
    const query = buildListQuery(params);
    const url = query
      ? `${API_ENDPOINTS.WORKFLOWS.RUNS(encodeURIComponent(workflowId))}?${query}`
      : API_ENDPOINTS.WORKFLOWS.RUNS(encodeURIComponent(workflowId));
    const result = await apiClient.get<ApiResponse<Paginated<WorkflowRunSummary>>>(url, { authenticate: true });
    return (result as any).data ?? result;
  },

  async getRunById(runId: string): Promise<WorkflowRunDetail> {
    const result = await apiClient.get<ApiResponse<WorkflowRunDetail>>(
      API_ENDPOINTS.WORKFLOWS.RUN_BY_ID(encodeURIComponent(runId)),
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async getRunEvents(runId: string): Promise<WorkflowRunEvent[]> {
    const result = await apiClient.get<ApiResponse<{ items: WorkflowRunEvent[] }>>(
      API_ENDPOINTS.WORKFLOWS.RUN_EVENTS(encodeURIComponent(runId)),
      { authenticate: true }
    );
    const payload = (result as any).data ?? result;
    return payload.items ?? [];
  },

  async createRun(workflowId: string, payload: CreateWorkflowRunInput): Promise<WorkflowRunDetail> {
    const result = await apiClient.post<ApiResponse<WorkflowRunDetail>>(
      API_ENDPOINTS.WORKFLOWS.RUNS(encodeURIComponent(workflowId)),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  /** Fetch the turn-by-turn transcript for a conversation (used by the
   *  Conversations drawer). Reuses the shared conversation-history endpoint. */
  async getConversationTranscript(
    conversationId: string,
  ): Promise<Array<{ id: string; senderType: string; message: string; createdAt: string }>> {
    const url = `/core/conversation-history/by-conversation/${encodeURIComponent(conversationId)}`;
    const result = await apiClient.get<ApiResponse<any>>(url, { authenticate: true });
    const payload = (result as any).data ?? result;
    const items = payload?.items ?? payload ?? [];
    return Array.isArray(items) ? items : [];
  },
};
